const React = require('react');
const { useCore } = require('stremio/core');
const { planManagedAddons } = require('./plan');
const { managedTransport, FROST } = require('./transport');

const STORAGE_KEY = 'caroline.managedAddonsInstalled';

const readManagedUrls = (storageKey) => {
    try {
        const value = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        return Array.isArray(value) ? value.filter((url) => typeof url === 'string') : [];
    } catch (_) {
        return [];
    }
};

const saveManagedUrls = (storageKey, urls) => {
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(urls));
    } catch (_) {
        // The Core profile remains the source of installed addons when storage is unavailable.
    }
};

const loadAddon = (core, transportUrl) => new Promise((resolve, reject) => {
    let settled = false;
    const finish = (value, error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        core.off('state', onState);
        error ? reject(error) : resolve(value);
    };
    const onState = async (models) => {
        if (!models.includes('addon_details') || settled) return;
        try {
            const state = await core.transport.getState('addon_details');
            if (state?.selected?.transportUrl !== transportUrl) return;
            const remote = state?.remoteAddon;
            if (remote?.content?.type === 'Ready' && remote.content.content?.transportUrl === transportUrl) {
                finish(remote.content.content);
            } else if (remote?.content?.type === 'Err') {
                finish(null, new Error('Addon manifest could not be loaded.'));
            }
        } catch (error) {
            finish(null, error);
        }
    };
    const timeout = setTimeout(() => finish(null, new Error('Addon manifest timed out.')), 12000);
    core.on('state', onState);
    core.transport.dispatch({
        action: 'Load',
        args: { model: 'AddonDetails', args: { transportUrl } }
    }, 'addon_details').then(() => onState(['addon_details']), (error) => finish(null, error));
});

const getInstalledAddons = async (core) => {
    const ctx = await core.transport.getState('ctx');
    return Array.isArray(ctx?.profile?.addons) ? ctx.profile.addons : [];
};

const getProfileStorageKey = async (core) => {
    const ctx = await core.transport.getState('ctx');
    const userId = ctx?.profile?.auth?.user?._id;
    return `${STORAGE_KEY}:${typeof userId === 'string' ? userId : 'local'}`;
};

const ManagedAddonsSync = () => {
    const core = useCore();
    const syncing = React.useRef(null);

    React.useEffect(() => {
        let active = true;

        const sync = async () => {
            // React StrictMode can cancel the first effect while its request is
            // pending. The replacement effect must wait and then run its own sync.
            if (syncing.current) {
                await syncing.current;
                if (active) return sync();
                return;
            }
            if (!active) return;
            let release;
            syncing.current = new Promise((resolve) => { release = resolve; });
            try {
                const response = await fetch('/api/addons', { credentials: 'same-origin', cache: 'no-store' });
                if (!response.ok || !active) return;
                const data = await response.json();
                if (!Array.isArray(data.items) || !Array.isArray(data.disabledItems) || !active) return;

                const approved = new Set(data.items.map((item) => item.transportUrl).filter((url) => typeof url === 'string').map((url) => managedTransport(url)));
                const disabled = new Set(data.disabledItems.map((item) => item.transportUrl).filter((url) => typeof url === 'string').map((url) => managedTransport(url)));
                const storageKey = await getProfileStorageKey(core);
                let installed = await getInstalledAddons(core);
                const plan = planManagedAddons([...approved], [...disabled], readManagedUrls(storageKey), installed);
                // Replace this one direct browser transport rather than keeping
                // duplicate addon installations/resources in the Core profile.
                if (installed.some((item) => item.transportUrl === FROST) &&
                    (approved.has(managedTransport(FROST)) || disabled.has(managedTransport(FROST)))) plan.remove.push(FROST);
                const managed = new Set(plan.keepTracked);

                for (const url of plan.remove) {
                    if (!active) return;
                    if (await getProfileStorageKey(core) !== storageKey) return;
                    if (url === FROST && approved.has(managedTransport(FROST)) &&
                        !installed.some((item) => item.transportUrl === managedTransport(FROST))) continue;
                    const addon = installed.find((item) => item.transportUrl === url);
                    if (addon) {
                        await core.transport.dispatch({ action: 'Ctx', args: { action: 'UninstallAddon', args: addon } });
                    }
                }
                saveManagedUrls(storageKey, [...managed]);

                for (const url of plan.install) {
                    if (!active) return;
                    if (await getProfileStorageKey(core) !== storageKey) return;
                    installed = await getInstalledAddons(core);
                    if (installed.some((item) => item.transportUrl === url)) continue;
                    try {
                        const addon = await loadAddon(core, url);
                        if (!active) return;
                        if (await getProfileStorageKey(core) !== storageKey) return;
                        await core.transport.dispatch({ action: 'Ctx', args: { action: 'InstallAddon', args: addon } });
                        if (url === managedTransport(FROST)) {
                            const direct = installed.find((item) => item.transportUrl === FROST);
                            if (direct) await core.transport.dispatch({ action: 'Ctx', args: { action: 'UninstallAddon', args: direct } });
                        }
                        managed.add(url);
                        saveManagedUrls(storageKey, [...managed]);
                    } catch (_) {
                        // One invalid addon must not stop the remaining approved addons.
                    }
                }
            } catch (_) {
                // A temporarily unavailable Caroline API leaves the existing Core profile intact.
            } finally {
                syncing.current = null;
                release();
            }
        };

        sync();
        window.addEventListener('focus', sync);
        return () => {
            active = false;
            window.removeEventListener('focus', sync);
        };
    }, [core]);

    return null;
};

module.exports = ManagedAddonsSync;
